import React from 'react';

import {Icons, Sizes} from '@pb/kit-component-icon';

import {BasicIconButton} from './basic-icon-button.component';

import type {BasicIconButtonProps} from './basic-icon-button.component';


export type InfoIconButtonProps = BasicIconButtonProps;

/**
 * Icon-only "info" button — a focusable trigger that renders the `INFO` icon
 * with the legacy `Info` component's visual treatment (dark-gray icon that
 * turns sky-blue on hover). It does **not** include a `Tooltip` or `Dropdown`;
 * compose those around it via their `anchor` render prop when you need an
 * overlay.
 *
 * `InfoIconButton` is a replacement for the deprecated `@pb/kit-component-info`
 * `Info` component. Unlike `Info`, it renders a real focusable `<button>`
 * (keyboard-accessible out of the box) and leaves overlay responsibility to
 * `Tooltip` (`@pb/kit-component-popups`) or `Dropdown` (`@pb/kit-component-dropdown-v2`).
 *
 * Pass anchor props (`aria-labelledby`, the ref, etc.) straight through —
 * `BasicIconButton` forwards any unknown props to the underlying element.
 *
 * ## Accessibility
 * - Always supply an accessible name (via `aria-label`, `aria-labelledby`, or a
 *   wrapping label) — the button has no visible text.
 * - When used as a `Tooltip` anchor, the tooltip sets `aria-describedby`
 *   automatically via `getAnchorProps`.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/buttons-info-icon--info-icon-with-tooltip}
 */
export function InfoIconButton(props: InfoIconButtonProps) {
    const {
        iconProps,
        ...otherProps
    } = props;

    return (
        <BasicIconButton
            aria-label="Info"
            {...otherProps}
            iconProps={{
                type: Icons.INFO,
                size: Sizes.Size_12,
                ...iconProps,
            }}
        />
    );
}
